// Configuration API
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_EP_URI;

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused';

export const SESSION_TYPES = ['CM', 'TD', 'TP', 'Projet', 'CCS', 'Autre'] as const;

export interface CourseSession {
  id: number;
  session_type: string;
  label: string | null;
  date: string;
  start_time: string | null;
  end_time: string | null;
  room: string | null;
  td_group: string | null;
  teachers: { id: number; lastname: string | null; firstname: string | null }[];
  teachers_label: string | null;
  remark: string | null;
  is_past?: boolean;
  can_record?: boolean;
}

export interface AttendanceSummary {
  present: number;
  absent: number;
  late: number;
  excused: number;
  past: number;
  unrecorded: number;
  upcoming: number;
}

export interface MyAttendance {
  sessions: (CourseSession & { status: AttendanceStatus | null; is_past: boolean })[];
  summary: AttendanceSummary;
}

export interface AttendanceOverview {
  sessions: CourseSession[];
  students: { id: number; lastname: string | null; firstname: string | null; td_group: string | null; summary: AttendanceSummary }[];
  records: { session_id: number; user_id: number; status: AttendanceStatus }[];
}

export interface RollCall {
  session: CourseSession;
  students: { id: number; lastname: string | null; firstname: string | null; td_group: string | null; status: AttendanceStatus | null }[];
}

export interface SessionImportRow {
  line: number;
  type: string;
  label: string;
  date?: string;
  audience: string;
  td_group?: string | null;
  teachers?: string[];
  teachers_label: string;
  status: string;
  message: string;
  warnings: string[];
}

export interface SessionImportResult {
  message: string;
  rows: SessionImportRow[];
  unresolved_groups: string[];
  unknown_teachers: string[];
  missing_sessions: CourseSession[];
  deleted: number;
  apply: boolean;
}

export interface PhotoStats {
  students: number;
  with_photo: number;
  without_student_number: number;
  missing: { id: number; lastname: string | null; firstname: string | null; td_group: string | null; student_number: string | null }[];
}

export type CourseSessionInput = {
  session_type: string;
  date: string;
  start_time: string | null;
  end_time: string | null;
  td_group: string | null;
  label: string | null;
  room: string | null;
  teacher_ids: number[];
};

async function callApi(path: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', accessToken: string, body?: any): Promise<any> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json.message || `HTTP error! status: ${response.status}`);
  }

  return json;
}

class AttendanceService {

  async getMine(accessToken: string): Promise<MyAttendance> {
    return callApi('/attendance/me', 'GET', accessToken);
  }

  async getOverview(filters: { td_group?: string; student_id?: number }, accessToken: string): Promise<AttendanceOverview> {
    const params = new URLSearchParams();
    if (filters.td_group) params.set('td_group', filters.td_group);
    if (filters.student_id) params.set('student_id', String(filters.student_id));
    return callApi(`/attendance/overview?${params.toString()}`, 'GET', accessToken);
  }

  async listSessions(accessToken: string): Promise<CourseSession[]> {
    return callApi('/attendance/sessions', 'GET', accessToken);
  }

  async getRollCall(sessionId: number, accessToken: string): Promise<RollCall> {
    return callApi(`/attendance/sessions/${sessionId}/roll`, 'GET', accessToken);
  }

  async saveRollCall(sessionId: number, records: { user_id: number; status: AttendanceStatus | null }[], accessToken: string): Promise<{ message: string }> {
    return callApi(`/attendance/sessions/${sessionId}/roll`, 'PUT', accessToken, { records });
  }

  async createSession(data: CourseSessionInput, accessToken: string): Promise<{ message: string }> {
    return callApi('/admin/course-sessions', 'POST', accessToken, data);
  }

  async updateSession(sessionId: number, data: CourseSessionInput, accessToken: string): Promise<{ message: string }> {
    return callApi(`/admin/course-sessions/${sessionId}`, 'PUT', accessToken, data);
  }

  async deleteSession(sessionId: number, accessToken: string): Promise<{ message: string }> {
    return callApi(`/admin/course-sessions/${sessionId}`, 'DELETE', accessToken);
  }

  // Photo d'un·e étudiant·e (null si aucune) — <img> ne sait pas envoyer le jeton, d'où le blob
  async getPhoto(userId: number, accessToken: string): Promise<Blob | null> {
    const response = await fetch(`${API_BASE_URL}/attendance/photos/${userId}`, {
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  }

  async getPhotoStats(accessToken: string): Promise<PhotoStats> {
    return callApi('/admin/student-photos', 'GET', accessToken);
  }

  async uploadPhotos(files: File[], accessToken: string): Promise<{ message: string; stored: number; ignored: { name: string; reason: string }[] }> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await fetch(`${API_BASE_URL}/admin/student-photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${accessToken}` },
      body: formData,
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json;
  }

  async importSessions(file: File, options: { apply: boolean; groupMap: Record<string, string>; deleteMissing: boolean }, accessToken: string): Promise<SessionImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('apply', options.apply ? 'true' : 'false');
    formData.append('group_map', JSON.stringify(options.groupMap));
    formData.append('delete_missing', options.deleteMissing ? 'true' : 'false');

    const response = await fetch(`${API_BASE_URL}/admin/course-sessions/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: formData,
    });

    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json;
  }
}

export const attendanceService = new AttendanceService();
